import { DocumentNode, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ActivatePurchaseMutation from './ActivatePurchaseMutation';
import DeactivatePurchaseMutation from './DeactivatePurchaseMutation';

interface UseTogglePurchaseMutationsProps {
  onActivated?: () => void;
  onDeactivated?: () => void;
  refetchQueries?: DocumentNode[];
}

interface Purchase {
  id?: string | null;
  batch_size: number;
}

const useTogglePurchaseMutations = ({
  onActivated, onDeactivated, refetchQueries,
}: UseTogglePurchaseMutationsProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [activate] = useMutation(
    ActivatePurchaseMutation,
    {
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        onActivated?.();
        notifier.success(t('notifications.purchase_activated'));
      },
    });

  const [deactivate] = useMutation(
    DeactivatePurchaseMutation,
    {
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        onDeactivated?.();
        notifier.success(t('notifications.purchase_deactivated'));
      },
    },
  );

  const activatePurchase = (purchase: Purchase) => {
    activate({ variables: { id: purchase.id } });
  };

  const deactivatePurchase = (purchase: Purchase) => {
    deactivate({ variables: { id: purchase.id } });
  };

  return { activatePurchase, deactivatePurchase };
};

export default useTogglePurchaseMutations;
